import { cn } from '@/lib/utils';
import { AlertTriangle, CheckCircle2, CircleSlash, XCircle } from 'lucide-react';

export type StoplightValue = 'green' | 'amber' | 'red' | 'neutral';

const PRESENTATION = {
  green: {
    label: 'Green',
    Icon: CheckCircle2,
    className: 'text-green-700 dark:text-green-300',
  },
  amber: {
    label: 'Amber',
    Icon: AlertTriangle,
    className: 'text-amber-700 dark:text-amber-300',
  },
  red: {
    label: 'Red',
    Icon: XCircle,
    className: 'text-red-700 dark:text-red-300',
  },
  neutral: {
    label: 'N/A',
    Icon: CircleSlash,
    className: 'text-gray-600 dark:text-gray-300',
  },
} as const;

interface StoplightProps {
  value: StoplightValue;
  reason: string;
  className?: string;
}

/**
 * A presentational rendering of a previously resolved platform-health result.
 * Health state inference belongs to the Overview resolver, not this component.
 */
export function Stoplight({ value, reason, className }: StoplightProps) {
  const { label, Icon, className: colorClassName } = PRESENTATION[value];

  return (
    <span
      role="status"
      aria-label={`${label} — ${reason}`}
      className={cn('inline-flex min-w-0 flex-wrap items-start gap-x-1.5 gap-y-0.5', className)}
    >
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', colorClassName)} aria-hidden="true" />
      <span className={cn('font-semibold', colorClassName)}>{label}</span>
      <span className="min-w-0 break-words text-xs text-gray-600 dark:text-gray-300">
        — {reason}
      </span>
    </span>
  );
}
